<template>
  <div class="keywords">
    <text-input
      v-if="showInput"
      v-model:value="inputValue"
      :placeholder="placeholder"
      :character-limit="maxLength"
      :disabled="disabled"
      :error-message="invalidMessage"
      :custom-validation="true"
      :use-secondary-character-limit="true"
      :secondary-character-limit="secondaryCharacterLimit"
      :text-id="textId"
      :mandatory="mandatory"
      @input="handleInput"
      @keyup="handleKeyUp"
      @keydown.tab.prevent
      @blur="handleBlur"
      @error-updated="handleErrorUpdated"
    />
    <cv-tag
      v-for="(tag, index) in tagsArray"
      :key="index"
      kind="blue"
      :label="tag"
      :disabled="disabled"
      :class="[existingIndex.includes(index) ? 'focus' : '']"
      :filter="showInput && !nonRemoveableTagsArray.includes(tag)"
      :clear-aria-label="tagRemoveLabel"
      @remove="handleRemove(tag)"
    />
  </div>
</template>

<script setup lang="ts">
import { validateMaxCharacterLimit } from "@plugins/common/utils/text-input-validation-helper.ts";
import { ADD_FROM_EXISTING_TYPE } from "@modules/editor/service/editor.constant";
import { isEmpty, notDefined } from "@utils/var.check";
import { ref, computed, watch, onMounted, inject } from "vue";
import { useI18n } from "vue-i18n";

const searchContext = inject("searchContext");

// Props
interface Props {
  tags?: string;
  placeholder?: string;
  characterLimit?: number;
  tagCharacterLimit?: number;
  disabled?: boolean;
  mode?: string;
  delimiter?: string;
  keys?: {
    enter: { keyCode: number; shiftKey: boolean };
    comma: { keyCode: number; shiftKey: boolean };
  };
  errorMessage?: string;
  message?: string;
  textId?: string;
  mandatory?: boolean;
  maxNumberOfTagsAllowed?: number;
  existingKeywords?: unknown[];
  isCaseSensitive?: boolean;
  duplicatedErrorMsg?: string;
  exceedTagErrorMsg?: string;
  keywordExceedErrorMsg?: string;
  nonRemoveableTags?: string;
  tagRemoveLabel?: string;
}

const {
  tags = "",
  placeholder = "",
  characterLimit = 250,
  tagCharacterLimit = 250,
  disabled = false,
  mode = "VIEW",
  delimiter = ",",
  keys = {
    enter: { keyCode: 13, shiftKey: false },
    comma: { keyCode: 188, shiftKey: false },
  },
  errorMessage = "",
  message = "",
  textId = "",
  mandatory = false,
  maxNumberOfTagsAllowed = undefined, // to cater for convertToTags logic
  existingKeywords = [],
  isCaseSensitive = true,
  duplicatedErrorMsg = "",
  exceedTagErrorMsg = "",
  keywordExceedErrorMsg = "keywords_total_character_limit_invalid_message",
  nonRemoveableTags = "",
  tagRemoveLabel = undefined,
} = defineProps<Props>();

// Emits
const emit = defineEmits(["errorUpdated", "input", "remove", "keywordError", "executed"]);

// State
const inputValue = ref("");
const tagsValue = ref(tags);
const invalid = ref(true);
const invalidMessage = ref("");
const existingIndex = ref([]);

const characterLimitValue = ref(characterLimit);
const tagCharacterLimitValue = ref(tagCharacterLimit);

const { t } = useI18n();

// Computed
const tagsArray = computed(() =>
  isEmpty(tagsValue.value) ? [] : tagsValue.value.split(delimiter),
);

const showInput = computed(() => mode === "EDIT");

const maxLength = computed(() => {
  return tagsValue.value && characterLimitValue.value > 0
    ? characterLimitValue.value - tagsValue.value.length
    : 0;
});

const isFromGamification = computed(() => {
  return searchContext?.addFromType === ADD_FROM_EXISTING_TYPE.GAME_BOOK;
});

const secondaryCharacterLimit = computed(() => {
  return isFromGamification.value ? tagCharacterLimit : characterLimit;
});

const nonRemoveableTagsArray = computed(() =>
  nonRemoveableTags === "" ? [] : nonRemoveableTags.split(delimiter),
);

// Methods
const handleErrorUpdated = (value: string) => {
  emit("errorUpdated", value);
};

const handleBlur = (e: Event) => {
  e.stopPropagation();
  const val = (e.target as HTMLInputElement).value;
  if (!validateInput(val, true)) return;
  convertToTags(val);
  emit("input", tagsValue.value);
};

const validate = (value: string, characterLimit, triggeredFromRemoveTagOrInput) => {
  if (notDefined(characterLimit)) return true;
  if (mandatory && isEmpty(tagsValue.value) && isEmpty(value) && !triggeredFromRemoveTagOrInput) {
    invalidMessage.value = t("field_empty_error_message");
    return;
  }
  if (validateMaxCharacterLimit(value, characterLimit)) {
    invalid.value = true;
    invalidMessage.value = "";
  } else {
    invalid.value = false;
    const invalidMessageString = t(keywordExceedErrorMsg, {
      num: characterLimit,
    });
    if (invalidMessage.value !== invalidMessageString) {
      invalidMessage.value = invalidMessageString;
    }
    emit("keywordError", "exceed.character");
  }
  return invalid.value;
};

const handleKeyUp = (e: KeyboardEvent) => {
  if (!validateInput((e.target as HTMLInputElement).value, true)) return;

  const delimiterFound = Object.values(keys).some(
    (key) => key.keyCode === e.keyCode && key.shiftKey === e.shiftKey,
  );
  if (delimiterFound) {
    convertToTags((e.target as HTMLInputElement).value);
  }
  emit("input", tagsValue.value);
};

const handleInput = function (value) {
  inputValue.value = value;
};

const handleRemove = (tag: string) => {
  const arr = [...tagsArray.value];
  arr.splice(arr.indexOf(tag), 1);
  tagsValue.value = arr.join(delimiter);
  validateInput(inputValue.value, true);
  emit("remove", tagsValue.value);
};
const convertToTags = (content: string) => {
  if (!isEmpty(errorMessage)) return;

  existingIndex.value = [];
  const input = content.split(delimiter);
  let hasError = false;

  input.forEach((tag) => {
    const strippedTag = tag.trim();
    const tagsArr = tagsArray.value;

    if (tagsArr.indexOf(strippedTag) < 0 && strippedTag !== "") {
      // Check for duplicate keywords (case insensitive)
      if (
        !isCaseSensitive &&
        (existingKeywords.some(
          (existingKeyword) => existingKeyword.toLowerCase() === strippedTag.toLowerCase(),
        ) ||
          tagsArr.some(
            (existingKeyword) => existingKeyword.toLowerCase() === strippedTag.toLowerCase(),
          ))
      ) {
        invalidMessage.value = duplicatedErrorMsg;
        emit("keywordError", "duplicated");
        hasError = true;
        return;
      }

      // Check for more than max tags
      if (!isEmpty(maxNumberOfTagsAllowed) && tagsArr.length >= maxNumberOfTagsAllowed) {
        invalidMessage.value = exceedTagErrorMsg;
        emit("keywordError", "exceed.tag");
        hasError = true;
        return;
      }

      tagsArr.push(strippedTag);
    } else if (tagsArr.indexOf(strippedTag) > -1) {
      if (
        !isCaseSensitive &&
        tagsArr.some(
          (existingKeyword) => existingKeyword.toLowerCase() === strippedTag.toLowerCase(),
        )
      ) {
        invalidMessage.value = duplicatedErrorMsg;
        emit("keywordError", "duplicated");
        hasError = true;
        return;
      }

      existingIndex.value.push(tagsArr.indexOf(strippedTag));
    }
  });

  if (!hasError) {
    inputValue.value = "";
    tagsValue.value = tagsArray.value.join(delimiter);
  }
};

const handleSave = () => {
  if (!validateInput(inputValue.value.trim(), false)) {
    emit("executed", { code: "error", data: [], errors: [] });
    return;
  }
  if (inputValue.value.trim() !== "") {
    convertToTags(inputValue.value);
  }
  emit("executed", { code: "ok", data: tagsValue.value, errors: [] });
};

const validateInput = (inputValue, triggeredFromRemoveTagOrInput) => {
  let fullTag = tagsValue.value;
  if (!validate(inputValue, secondaryCharacterLimit.value, triggeredFromRemoveTagOrInput))
    return false;

  const formattedValue = inputValue.replaceAll(`${delimiter} `, delimiter).trim();
  if (tagsValue.value && tagsValue.value.length && inputValue.length) {
    fullTag += `${delimiter}${formattedValue}`;
  } else if (inputValue.length) {
    fullTag = formattedValue;
  }
  return validate(fullTag, characterLimitValue.value, triggeredFromRemoveTagOrInput);
};

// Watch
watch(
  () => tags,
  (newTags) => {
    tagsValue.value = newTags;
  },
);

watch(
  () => errorMessage,
  (error) => {
    invalidMessage.value = error;
  },
);

watch(
  () => message,
  (msg) => {
    if (msg === "save") {
      handleSave();
    }
  },
);

// onMounted
onMounted(() => {
  tagCharacterLimitValue.value = Math.min(tagCharacterLimitValue.value, characterLimitValue.value);
});

defineExpose({
  validate: (value, characterLimit, triggeredFromRemoveTagOrInput) =>
    validate(value, characterLimit, triggeredFromRemoveTagOrInput),
});
</script>

<style lang="scss">
@import "@styles/abstracts/functions";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.keywords {
  &.focus {
    border: 2px solid $focus;
  }

  .cv-text-input {
    margin-bottom: $spacing-01;
  }

  .bx--tag {
    @include carbon--type-style("label-01", true);
    height: auto;
    margin-top: $spacing-03;
    padding: $spacing-01 $spacing-03;
    background-color: tint($interactive-01, 80%);
    color: $tag-text-01;

    &:first-child {
      margin-left: 0;
    }
  }

  .bx--tag--filter {
    padding-right: $spacing-01;
  }

  .bx--tag__label {
    max-width: initial;
    overflow: visible;
    white-space: normal;
    word-break: break-word;
  }

  .bx--tag--filter > svg {
    flex: 0 0 1.25rem;
  }
}
</style>
